import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  clampPressPosition,
  DEFAULT_LAYOUT,
  DEFAULT_PARAMS,
  DEFAULT_SPAWN_INTERVAL_SEC,
  LAYOUT_BOUNDS,
  MAX_TICK_DT,
} from "@/features/simulation/constants";
import {
  createInitialState,
  resumeConveyor as engineResumeConveyor,
  tick as engineTick,
  requestManualSpawn,
} from "@/features/simulation/engine";
import { createDefaultScene } from "@/features/simulation/scene";
import type { FactoryScene } from "@/features/simulation/scene-types";
import {
  DEFAULT_SIM_CONTEXT,
  type Layout,
  type SimulationContext,
  type SimulationParams,
  type SimulationState,
  type SimulationStats,
  type SpawnMode,
} from "@/features/simulation/types";
import { clampInt } from "@/lib/math/clamp";

/**
 * tick 時に渡す `SimulationContext` の供給関数。
 * sim-network-bridge から `configureSimContextProvider()` で差し替えられる。
 * 既定は全許可（network 連携が無くても従来通り動く）。
 */
let simContextProvider: () => SimulationContext = () => DEFAULT_SIM_CONTEXT;

/** bridge 層からコンテキスト供給関数を差し替えるための公開 API */
export function configureSimContextProvider(fn: () => SimulationContext): void {
  simContextProvider = fn;
}

/**
 * Zustand ストア設計メモ:
 *
 * - "mutable sim state"（module-level ref）→ Pixi レンダラが毎フレーム直接参照
 * - "React-reactive slice"（このストア）   → UI が購読する (params/stats/running 等)
 *
 * レイアウトを変更した場合は、既存の機械配置/ワーク位置と整合性を取れないため
 * 自動リセット（Q4-2 a）。renderer の再構築も必要なので layoutVersion をバンプし、
 * FactoryMap 側で PixiStage の key として使う。
 */

/** モジュールレベルの可変参照（Pixi 描画ループが直接参照する） */
const simStateRef: { current: SimulationState } = {
  current: createInitialState({ layout: DEFAULT_LAYOUT }),
};

export function getSimState(): SimulationState {
  return simStateRef.current;
}

export type SpeedMultiplier = 1 | 2 | 4;

interface SimulationStoreState {
  params: SimulationParams;
  /** Layout は scene の第 1 オブジェクトから派生する互換ビュー (UI スライダ用) */
  layout: Layout;
  /** 真の配置情報 — エディタが操作する対象 */
  scene: FactoryScene;
  /** scene 変更のたびにインクリメント — PixiStage の React key として使用 (旧 layoutVersion) */
  layoutVersion: number;
  spawnMode: SpawnMode;
  running: boolean;
  speed: SpeedMultiplier;
  stats: SimulationStats;
  queuedSpawns: number;
  /** 自動停止フラグ（UI 表示・再開ボタン表示用） */
  conveyorStopped: boolean;
}

interface SimulationStoreActions {
  start(): void;
  pause(): void;
  reset(): void;
  setParams(partial: Partial<SimulationParams>): void;
  setLayout(partial: Partial<Layout>): void;
  /**
   * エディタから新 Scene を反映する (sim をリセット)。
   * 編集中はエディタ側のドラフトを保持し、保存ボタンで初めてここを呼ぶ。
   */
  setScene(scene: FactoryScene): void;
  setSpawnMode(mode: SpawnMode): void;
  setSpeed(s: SpeedMultiplier): void;
  requestSpawn(): void;
  /** 自動停止中のコンベアを手動で再開する */
  resumeConveyor(): void;
  advance(dtRealSec: number): void;
}

export type SimulationStore = SimulationStoreState & SimulationStoreActions;

export const useSimulationStore = create<SimulationStore>()(
  devtools(
    (set, get) => ({
      params: { ...DEFAULT_PARAMS },
      layout: { ...DEFAULT_LAYOUT },
      scene: createDefaultScene(),
      layoutVersion: 0,
      spawnMode: { kind: "auto", intervalSec: DEFAULT_SPAWN_INTERVAL_SEC },
      running: false,
      speed: 1,
      stats: { ...simStateRef.current.stats, recentCompletionTimes: [] },
      queuedSpawns: 0,
      conveyorStopped: false,

      start() {
        set({ running: true }, false, "sim/start");
      },

      pause() {
        set({ running: false }, false, "sim/pause");
      },

      reset() {
        const { spawnMode, scene } = get();
        simStateRef.current = createInitialState({ spawnMode, scene });
        set(
          {
            running: false,
            stats: { ...simStateRef.current.stats },
            queuedSpawns: 0,
            conveyorStopped: false,
            layoutVersion: get().layoutVersion + 1,
          },
          false,
          "sim/reset",
        );
      },

      setParams(partial) {
        set((s) => ({ params: { ...s.params, ...partial } }), false, "sim/setParams");
      },

      setLayout(partial) {
        const prev = get().layout;
        const nextConveyor = partial.conveyorLength ?? prev.conveyorLength;
        const nextPress = clampPressPosition(partial.pressPosition ?? prev.pressPosition, nextConveyor);
        const nextBuffer = clampInt(
          partial.bufferCapacity ?? prev.bufferCapacity,
          LAYOUT_BOUNDS.bufferCapacity.min,
          LAYOUT_BOUNDS.bufferCapacity.max,
        );
        const nextTransport = clampInt(
          partial.transportCapacity ?? prev.transportCapacity,
          LAYOUT_BOUNDS.transportCapacity.min,
          LAYOUT_BOUNDS.transportCapacity.max,
        );
        const next: Layout = {
          conveyorLength: nextConveyor,
          pressPosition: nextPress,
          bufferCapacity: nextBuffer,
          transportCapacity: nextTransport,
        };

        if (
          next.conveyorLength === prev.conveyorLength &&
          next.pressPosition === prev.pressPosition &&
          next.bufferCapacity === prev.bufferCapacity &&
          next.transportCapacity === prev.transportCapacity
        ) {
          return;
        }

        const { spawnMode } = get();
        simStateRef.current = createInitialState({ spawnMode, layout: next });
        set(
          {
            layout: next,
            // Layout 経由のスライダ変更は scene も更新する (createInitialState の戻り値)
            scene: simStateRef.current.scene,
            running: false,
            stats: { ...simStateRef.current.stats },
            queuedSpawns: 0,
            conveyorStopped: false,
            layoutVersion: get().layoutVersion + 1,
          },
          false,
          "sim/setLayout",
        );
      },

      setScene(scene) {
        const { spawnMode } = get();
        simStateRef.current = createInitialState({ spawnMode, scene });
        set(
          {
            scene: simStateRef.current.scene,
            layout: simStateRef.current.layout,
            running: false,
            stats: { ...simStateRef.current.stats },
            queuedSpawns: 0,
            conveyorStopped: false,
            layoutVersion: get().layoutVersion + 1,
          },
          false,
          "sim/setScene",
        );
      },

      setSpawnMode(mode) {
        simStateRef.current.spawnMode = mode;
        simStateRef.current.spawnTimer = 0;
        simStateRef.current.queuedSpawns = 0;
        set({ spawnMode: mode, queuedSpawns: 0 }, false, "sim/setSpawnMode");
      },

      setSpeed(s) {
        set({ speed: s }, false, "sim/setSpeed");
      },

      requestSpawn() {
        requestManualSpawn(simStateRef.current);
        set({ queuedSpawns: simStateRef.current.queuedSpawns }, false, "sim/requestSpawn");
      },

      resumeConveyor() {
        engineResumeConveyor(simStateRef.current);
        set({ conveyorStopped: false }, false, "sim/resumeConveyor");
      },

      advance(dtRealSec) {
        const { running, speed, params } = get();
        if (!running) return;
        const dt = Math.min(MAX_TICK_DT, dtRealSec * speed);
        engineTick(simStateRef.current, params, dt, simContextProvider());

        const sim = simStateRef.current;
        // conveyorStopped が変化したときだけ UI に通知するのは面倒なので毎 tick set するが、
        // Zustand のオブジェクト同値比較で実質 no-op になるケースが多い。
        set(
          {
            stats: {
              completedCount: sim.stats.completedCount,
              lastCycleTimeSec: sim.stats.lastCycleTimeSec,
              throughputPerMin: sim.stats.throughputPerMin,
              recentCompletionTimes: sim.stats.recentCompletionTimes,
            },
            queuedSpawns: sim.queuedSpawns,
            conveyorStopped: sim.conveyorStopped,
          },
          false,
          "sim/advance",
        );
      },
    }),
    { name: "SimulationStore" },
  ),
);
